import { useState } from 'react'
import { Head, router } from '@inertiajs/react'
import AppLayout from '@/components/AppLayout.tsx'
import { DatePicker } from '@/components/ui/DatePicker'
import PlanHeader from '../components/PlanHeader'
import PlanBreakdownSections, { type ChannelSectionData, type QuarterlySectionData } from './components/PlanBreakdownSections'
import RowCommentThread from './components/RowCommentThread'
import OrphanCommentsBlock from './components/OrphanCommentsBlock'
import RequestPeopleCard from './components/RequestPeopleCard'
import SubmitMessageCard from './components/SubmitMessageCard'
import { type PlanNodeFromServer } from './components/VersionViewOverallSection'
import { rowKey } from './components/rowAnchor'
import { type RequestPage } from './components/revisionRequest'
import { useRequestComments } from './components/useRequestComments'
import { type Person } from '@/lib/person'
import { plan_top_down_revision_request_path } from '@/lib/routes'
import { useMarket } from '@/lib/useMarket'

interface Props {
  slug: string
  begin_date: string
  end_date: string
  top_down_plan: { id: string; name: string; slug: string; revenue_target: number; currency: string | null; org_node: { id: string; name: string; slug: string } | null }
  plan_nodes: PlanNodeFromServer[]
  channel_section: ChannelSectionData
  quarterly_section: QuarterlySectionData
  request: RequestPage
  people: Person[]
  default_reviewer_ids: string[]
  default_due_on: string
}

// The task form, like the submit page's "Open a pull request": message and
// due date on the left, who does the work and who reviews on the right, the
// comments under their rows below. Sending turns the draft into a task.
export default function RevisionRequestNew({ slug, begin_date, end_date, top_down_plan, plan_nodes, channel_section, quarterly_section, request, people, default_reviewer_ids, default_due_on }: Props) {
  const market = useMarket()
  const [message, setMessage] = useState('')
  const [dueOn, setDueOn] = useState(default_due_on)
  const [picIds, setPicIds] = useState<string[]>([])
  const [reviewerIds, setReviewerIds] = useState<string[]>(default_reviewer_ids)
  const requestPath = plan_top_down_revision_request_path(market.slug, slug)

  const { byRow, orphans } = useRequestComments(request.comments)
  const ready = message.trim().length > 0 && picIds.length > 0 && dueOn.length > 0

  const send = () => router.post(requestPath, { message, due_on: dueOn, pic_ids: picIds, reviewer_ids: reviewerIds })

  return (
    <AppLayout header={<PlanHeader name={top_down_plan.name} orgNodeName={top_down_plan.org_node?.name ?? null} revenueTarget={top_down_plan.revenue_target} currency={top_down_plan.currency} beginDate={begin_date} endDate={end_date} />}>
      <Head title={`${top_down_plan.name} — Request revision`} />
      <div className="flex items-start gap-4">
        <div className="flex min-w-0 flex-col gap-4 lg:w-3/5">
          <SubmitMessageCard
            label="Message to the person in charge"
            placeholder="What needs to change and why"
            hint={ready ? null : 'A message and at least one person in charge are needed.'}
            warning={false}
            message={message}
            onChange={setMessage}
            submitLabel="Send revision request"
            onSubmit={send}
            onCancel={() => router.visit(requestPath)}
            disabled={!ready}
          />
          <div style={{ background: 'var(--background)', borderColor: 'var(--border)' }} className="rounded-lg border p-5">
            <h2 className="text-lg font-semibold leading-snug tracking-tight text-foreground">Due date</h2>
            <p className="mt-0.5 text-[13px] text-muted-foreground">When the revised plan should be submitted.</p>
            <div className="mt-3 max-w-xs"><DatePicker value={dueOn} onChange={setDueOn} /></div>
          </div>
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-4">
          <RequestPeopleCard
            title="In charge"
            description="Who revises the plan. They get a task and a notification."
            pickerTitle="In charge"
            people={people}
            selectedIds={picIds}
            onChange={setPicIds}
            warning={picIds.length === 0 ? 'Pick at least one person.' : null}
          />
          <RequestPeopleCard
            title="Reviewers"
            description="Who approves the revised plan before it updates the published numbers."
            pickerTitle="Reviewers"
            people={people}
            selectedIds={reviewerIds}
            onChange={setReviewerIds}
            warning={reviewerIds.length === 0 ? 'No reviewer picked — the revised plan will be published as soon as it is submitted.' : null}
          />
        </div>
      </div>
      <h2 className="mt-6 mb-2 text-base font-semibold text-foreground">Your comments</h2>
      <PlanBreakdownSections
        planNodes={plan_nodes}
        channelSection={channel_section}
        quarterlySection={quarterly_section}
        currency={top_down_plan.currency}
        rowExtras={(anchor) => {
          const list = byRow.get(rowKey(anchor)) ?? []
          if (list.length === 0) return null
          return (
            <RowCommentThread anchor={anchor} comments={list} composerOpen={false} readOnly
              onOpenComposer={() => {}} onCloseComposer={() => {}} onCreate={() => {}} onUpdate={() => {}} onDelete={() => {}} />
          )
        }}
      />
      <OrphanCommentsBlock comments={orphans} readOnly />
    </AppLayout>
  )
}
